import { compare, relayKey, relayTitle } from "./bullseyeRelaySheet";
import { normalizeResultClass } from "../Leaderboard/bullseyeResultGrouping";

const text = value => value == null ? "" : String(value).trim();
const list = value => Array.isArray(value) ? value : [];
const numeric = value => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null;
const groupId = group => text(group?.gunGroupId || group?.groupId || group?.id);
const courseName = course => text(course?.courseGroupName || course?.courseName || course?.name || course?.title);
const courseId = course => text(course?.courseId) || courseName(course);
const targetText = value => text(value).replace(/^(?:firing point|point|target|lane)\s*#?\s*(\d+[a-z]?)$/i, "$1");
const unique = values => [...new Set(values.filter(Boolean))];
const inMatch = (row, matchId) => !matchId || !text(row?.matchId) || text(row.matchId) === matchId;
const emptyCell = (state = "not-scored") => {
  const label = { "not-scored": "Not scored", "not-entered": "Not entered", "entry-pending": "Entry pending", unavailable: "Breakdown unavailable" }[state];
  return { state, label, scoreLabel: label, score: null, xCount: null, shotsFired: null, plannedShots: null };
};

// A published zero is a score only when the producer supplies evidence that it
// was recorded. Bare zero-filled placeholders must remain visibly unscored.
export function bullseyeRelayScoreCell(source) {
  if (!source) return emptyCell();
  const score = numeric(source.totalScore ?? source.classificationScore ?? source.score?.aggregate);
  const xCount = numeric(source.xCount) ?? 0;
  const shotsFired = numeric(source.shotsFired);
  const plannedShots = numeric(source.plannedShots);
  const status = text(source.status || source.scoreStatus).toLowerCase();
  const recorded = (score !== null && score > 0) || xCount > 0 || shotsFired > 0 || numeric(source.completedScoreUnits) > 0
    || (score !== null && ["scored", "audited", "official", "confirmed", "complete", "completed"].includes(status));
  if (!recorded) return { ...emptyCell(), shotsFired, plannedShots };
  if (score === null) return { ...emptyCell("unavailable"), shotsFired, plannedShots };
  const label = `${score}–${xCount}X`;
  return { state: "scored", label, scoreLabel: label, score, xCount, shotsFired, plannedShots };
}

function sumPublishedCells(cells) {
  const entered = cells.filter(cell => cell.state !== "not-entered");
  if (entered.some(cell => cell.state === "unavailable")) return emptyCell("unavailable");
  const scored = entered.filter(cell => cell.state === "scored");
  if (!scored.length) return emptyCell(entered.length ? "not-scored" : "not-entered");
  const score = scored.reduce((sum, cell) => sum + cell.score, 0);
  const xCount = scored.reduce((sum, cell) => sum + cell.xCount, 0);
  const label = `${score}–${xCount}X`;
  const sumKnown = field => entered.every(cell => cell[field] !== null) ? entered.reduce((sum, cell) => sum + cell[field], 0) : null;
  return { state: "scored", label, scoreLabel: label, score, xCount, shotsFired: sumKnown("shotsFired"), plannedShots: sumKnown("plannedShots") };
}

function programGroups(setup, roster, scoreRows) {
  const groups = new Map();
  const addGroup = source => {
    const id = groupId(source);
    if (!id) return null;
    if (!groups.has(id)) groups.set(id, { id, label: text(source.title || source.firearmTitle || source.groupTitle) || "Firearm group", courses: [] });
    return groups.get(id);
  };
  const addCourse = (group, source) => {
    const id = courseId(source);
    if (group && id && !group.courses.some(course => course.id === id)) group.courses.push({ id, label: courseName(source) || "Course" });
  };
  [...list(setup?.gunGroups)].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)).forEach(source => {
    const group = addGroup(source);
    const courses = list(source.courses).length ? source.courses : [...list(source.stages)].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    courses.forEach(course => addCourse(group, course));
  });
  list(roster?.groups).forEach(addGroup);
  list(roster?.competitors).forEach(person => list(person.assignments).forEach(addGroup));
  scoreRows.forEach(row => list(row.firearms).forEach(source => {
    const group = addGroup(source);
    list(source.courses).forEach(course => addCourse(group, course));
  }));
  return [...groups.values()];
}

function addIndex(index, id, person) {
  if (!id) return;
  if (!index.has(id)) index.set(id, new Set());
  index.get(id).add(person);
}

function uniqueOwner(index, id) {
  const owners = index.get(id);
  return owners?.size === 1 ? [...owners][0] : null;
}

function identities(people) {
  const canonical = new Map(), aliases = new Map();
  people.forEach(person => {
    const id = text(person.participantId);
    addIndex(canonical, id, person);
    // A legacy alias colliding with somebody else's canonical identity must
    // not become an apparently unambiguous legacy match.
    addIndex(aliases, id, person);
    list(person.legacyCompetitorIds).forEach(alias => addIndex(aliases, text(alias), person));
  });
  return row => {
    if (text(row.participantId)) return uniqueOwner(canonical, text(row.participantId));
    const ids = unique([text(row.competitorId), text(row.competitorExternalId)]);
    if (!ids.length) return null;
    const owners = ids.map(id => uniqueOwner(aliases, id));
    return owners.every(owner => owner && owner === owners[0]) ? owners[0] : null;
  };
}

// This fingerprint is solely a rendering key for orphaned records, never an
// identity join. Identity joins above use only explicit match-scoped IDs.
function fingerprint(value) {
  const input = JSON.stringify(value);
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) hash = Math.imul(hash ^ input.charCodeAt(i), 16777619);
  return (hash >>> 0).toString(36);
}

const scoreIdentity = source => {
  const field = ["resultId", "sk", "entryId", "competitorId", "participantId"].find(key => text(source[key]));
  return field ? `${field}:${text(source[field])}` : "";
};

function participation(person, score, profiles) {
  const categories = Array.isArray(score?.categories) ? score.categories : text(score?.category || score?.categories).split(",");
  return score?.twentyTwoOnly || person?.twentyTwoOnly || profiles.some(profile => profile.twentyTwoOnly)
    || categories.some(value => ["22only", "22caliberonly", "22calibreonly", "22onlyaggregate", "twentytwoonlyaggregate"].includes(text(value).toLowerCase().replace(/[^a-z0-9]/g, ""))) ? ".22 Only" : "Standard";
}

function displayProfile(profiles, source, field) {
  const values = unique(profiles.map(profile => text(profile[field])));
  if (!values.length && text(source?.[field])) values.push(text(source[field]));
  return values.map(value => field === "classification" ? normalizeResultClass(value)
    : ({ open: "Open", metallic: "Metallic" })[value.toLowerCase()] || value).join(" / ");
}

function positionRows(person, source, groups, personKey, assignmentUnavailable = false) {
  const positions = new Map();
  const sourceGroupIds = unique(list(source?.firearms).map(groupId));
  const assignments = list(person?.assignments);
  const addPosition = assignment => {
    const relay = relayKey(assignment.relayLabel), target = targetText(assignment.firingPointLabel);
    const key = JSON.stringify([relay, target.toLowerCase()]);
    if (!positions.has(key)) positions.set(key, { target, relay, relayLabel: text(assignment.relayLabel), profiles: [], scope: new Set(), wildcard: false });
    const position = positions.get(key), id = groupId(assignment);
    position.profiles.push(assignment);
    if (id) position.scope.add(id); else position.wildcard = true;
  };
  assignments.forEach(addPosition);
  if (!positions.size) addPosition({});
  const assigned = new Set(assignments.map(groupId).filter(Boolean));
  const hasWildcard = [...positions.values()].some(position => position.wildcard);
  if (!hasWildcard) sourceGroupIds.filter(id => !assigned.has(id)).forEach(id => addPosition({ gunGroupId: id }));
  [...positions.values()].forEach(position => {
    if (position.wildcard) sourceGroupIds.forEach(id => position.scope.add(id));
  });
  const allScope = unique([...positions.values()].flatMap(position => [...position.scope]));
  return [...positions.values()].map(position => {
    const profiles = position.profiles;
    const ids = [...position.scope];
    const groupLabel = groups.filter(group => ids.includes(group.id)).map(group => group.label).join(", ");
    const name = text(person?.rosterName || person?.displayName || source?.competitorName || source?.displayName) || "Competitor";
    const outcome = text(source?.outcomeStatus).toLowerCase();
    return { key: `${personKey}:${assignmentUnavailable ? "unavailable" : JSON.stringify([position.relay, position.target.toLowerCase()])}`,
      personKey, participantId: text(person?.participantId || source?.participantId), name,
      searchName: `${name} ${text(person?.displayName)} ${text(source?.competitorName || source?.displayName)}`,
      target: position.target, relayKey: assignmentUnavailable ? "unavailable" : position.relay ? `relay:${position.relay}` : "unassigned",
      relayLabel: position.relayLabel, groupIds: ids, groupLabel, groupTitle: groupLabel, hasPositionExceptions: positions.size > 1,
      division: displayProfile(profiles, source, "division"), classification: displayProfile(profiles, source, "classification"),
      participation: participation(person, source, profiles), outcomeLabel: ({ dq: "DQ", dnf: "DNF", withdrawn: "Withdrawn" })[outcome] || (source?.isRankEligible === false ? "Not ranked" : ""),
      statusLabel: ({ official: "Recorded scores confirmed", audited: "Audited", scored: "Scored", in_progress: "In progress", inProgress: "In progress", needs_refire: "Needs refire", unscored: "Not scored" })[text(source?.status)] || "",
      assignmentUnavailable, _score: source, _profiles: profiles, _allScope: allScope, _unknownEnrollment: hasWildcard && !sourceGroupIds.length,
    };
  });
}

function displayRow(row, groups, firearm) {
  const scoreGroups = list(row._score?.firearms);
  const aggregateOnly = Boolean(row._score && !scoreGroups.length && bullseyeRelayScoreCell(row._score).state === "scored");
  const entryPending = row._unknownEnrollment && !row._score;
  const displayed = groups.map(group => {
    const enrolled = row.groupIds.includes(group.id);
    const sources = scoreGroups.filter(source => groupId(source) === group.id);
    const source = sources.length === 1 ? sources[0] : null;
    const unavailable = sources.length > 1 || aggregateOnly || (row._unknownEnrollment && !source);
    const missing = entryPending ? "entry-pending" : row._unknownEnrollment ? "unavailable" : !enrolled ? "not-entered" : unavailable ? "unavailable" : "not-scored";
    const total = source && enrolled ? bullseyeRelayScoreCell(source) : emptyCell(missing);
    const courses = group.courses.map(course => {
      const courseSources = list(source?.courses).filter(item => courseId(item) === course.id);
      const courseSource = courseSources.length === 1 ? courseSources[0] : null;
      const cell = !enrolled ? emptyCell(missing) : courseSources.length > 1 ? emptyCell("unavailable")
        : courseSource ? bullseyeRelayScoreCell(courseSource)
          : emptyCell(source && total.state === "scored" ? "unavailable" : missing);
      return { id: course.id, ...cell, label: course.label, scoreLabel: cell.label };
    });
    return { id: group.id, label: group.label, total, courses };
  });
  const fullScope = firearm === "all" && !row.hasPositionExceptions;
  const total = entryPending ? emptyCell() : fullScope && row._score ? bullseyeRelayScoreCell(row._score)
    : aggregateOnly ? emptyCell("unavailable") : sumPublishedCells(displayed.map(group => group.total));
  const progressLabel = row.outcomeLabel || (entryPending ? "Awaiting scoring entry" : total.state === "scored" ? total.plannedShots > 0 && total.shotsFired !== null
    ? `${total.shotsFired}/${total.plannedShots} shots` : total.shotsFired !== null ? `${total.shotsFired} shots` : "Score received"
    : total.state === "unavailable" ? "Breakdown unavailable" : "Awaiting scores");
  return { ...row, total, groups: displayed, progressLabel };
}

/** Build a public, read-only score sheet. Never associate people by names or indexes. */
export function buildBullseyeRelayScores({ matchId = "", roster = null, scoreRows = [], setup = {}, relays = [], search = "", relay = "all", firearm = "all", firearmGroup } = {}) {
  const scores = [...new Map(list(scoreRows).filter(row => inMatch(row, matchId)).map(row => [JSON.stringify(row), row])).values()];
  const validRoster = inMatch(roster, matchId) ? roster : null;
  const people = list(validRoster?.competitors).filter(row => inMatch(row, matchId));
  const program = setup?.setupDetail || setup;
  const groups = programGroups(inMatch(program?.match || program, matchId) ? program : {}, validRoster, scores);
  const identityUnavailable = roster != null && roster.identityVersion !== 1;
  const ownerOf = identities(people);
  const byPerson = new Map(), unmatched = [];
  scores.forEach(score => {
    const person = identityUnavailable ? null : ownerOf(score);
    if (!person) { unmatched.push(score); return; }
    if (!byPerson.has(person)) byPerson.set(person, []);
    byPerson.get(person).push(score);
  });
  const sourceRows = [];
  people.forEach(person => {
    const matches = byPerson.get(person) || [];
    // Multiple different score records for one person are not additive. Keep
    // them visible for review rather than selecting or double-counting one.
    if (matches.length > 1) unmatched.push(...matches);
    const duplicatedId = text(person.participantId) && people.filter(candidate => text(candidate.participantId) === text(person.participantId)).length > 1;
    const personKey = `person:${text(person.participantId) || fingerprint(person)}${duplicatedId ? `:${fingerprint(person)}` : ""}`;
    sourceRows.push(...positionRows(person, matches.length === 1 ? matches[0] : null, groups, personKey));
  });
  unmatched.forEach(source => {
    const identity = scoreIdentity(source);
    const ambiguous = identity && unmatched.filter(item => scoreIdentity(item) === identity).length > 1;
    const key = `score:${identity || fingerprint(source)}${ambiguous ? `:${fingerprint(source)}` : ""}`;
    sourceRows.push(...positionRows(null, source, groups, key, true));
  });
  const model = { identityUnavailable, _sourceRows: sourceRows, _allGroups: groups, _relays: list(relays),
    hasScoreData: scores.some(score => bullseyeRelayScoreCell(score).state === "scored"),
    totalCompetitors: new Set(sourceRows.map(row => row.personKey)).size, totalAssignments: sourceRows.length };
  return filterBullseyeRelayScores(model, { search, relay, firearm: firearmGroup || firearm });
}

/** Filtering is pure and always starts from the full model, preserving view filters independently. */
export function filterBullseyeRelayScores(model, { search = "", relay = "all", firearm = "all", firearmGroup } = {}) {
  const selectedGroup = firearmGroup || firearm;
  const allGroups = model._allGroups || [];
  const activeFirearm = selectedGroup === "all" || allGroups.some(group => group.id === selectedGroup) ? selectedGroup : "all";
  const groups = allGroups.filter(group => activeFirearm === "all" || group.id === activeFirearm);
  const relayMap = new Map();
  model._relays.forEach(plan => { if (text(plan.label)) relayMap.set(`relay:${relayKey(plan.label)}`, { label: relayTitle(plan.label), plan }); });
  model._sourceRows.forEach(row => {
    if (!relayMap.has(row.relayKey)) relayMap.set(row.relayKey, { label: row.assignmentUnavailable ? "Assignment unavailable" : relayTitle(row.relayLabel), plan: null });
  });
  const relayKeys = [...relayMap.keys()].sort((a, b) => (a === "unavailable" ? 2 : a === "unassigned" ? 1 : 0) - (b === "unavailable" ? 2 : b === "unassigned" ? 1 : 0) || compare(a, b));
  const activeRelay = relay === "all" || relayMap.has(relay) ? relay : "all";
  const query = text(search).toLowerCase();
  const rows = model._sourceRows.filter(row => (!query || row.searchName.toLowerCase().includes(query))
    && (activeRelay === "all" || row.relayKey === activeRelay)
    && (activeFirearm === "all" || row.groupIds.includes(activeFirearm))).map(row => displayRow(row, groups, activeFirearm));
  const sections = relayKeys.map(key => ({ key, ...relayMap.get(key), rows: rows.filter(row => row.relayKey === key).sort((a, b) =>
    (!a.target && b.target ? 1 : a.target && !b.target ? -1 : compare(a.target, b.target)) || compare(a.name, b.name) || compare(a.key, b.key)) })).filter(section => section.rows.length);
  return { ...model, groups, firearmGroups: groups, sections, rows: sections.flatMap(section => section.rows), activeFirearm, activeRelay,
    firearmOptions: [{ value: "all", label: "All firearm groups" }, ...allGroups.map(group => ({ value: group.id, label: group.label }))],
    relayOptions: [{ value: "all", label: "All relays" }, ...relayKeys.map(key => ({ value: key, label: relayMap.get(key).label }))],
    visibleCompetitors: new Set(rows.map(row => row.personKey)).size };
}
